import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, Subject } from 'rxjs';
import { environment } from '../../../../../../environments/environment';
import { RequestTokenService } from '../../../../../../services/request-token.service';

@Injectable({
  providedIn: 'root'
})
export class ChatPerguntaRespostaService {
  private readonly executivo = `${environment.apiExecutivoUrl}ConversaOperacionalExecutivo`;
  private readonly operacional = `${environment.apiOperacionalUrl}ChatOperacional`;
  private readonly notificacoesVistas = new Set<string>();
  /** Avisos dispensados no painel do header (somente nesta sessão do navegador). */
  private readonly notificacoesOcultas = new Set<string>();
  readonly notificacaoVista$ = new Subject<string>();

  constructor(
    private http: HttpClient,
    private requestToken: RequestTokenService
  ) {}

  listarTipos(): Observable<any> {
    return this.http.get<any>(`${this.executivo}/ListarTiposPergunta`, this.opcoes());
  }

  /** Tipos ativos de TipoPerguntaOperacional (resposta da API executivo). */
  extrairTiposDaResposta(resposta: any): Array<{ Id: number; Nome: string }> {
    if (resposta?.success === false || resposta?.Success === false) {
      return [];
    }
    const payload = resposta?.data ?? resposta?.Data;
    const lista = Array.isArray(payload) ? payload : [];
    return lista
      .map((item: Record<string, unknown>) => ({
        Id: Number(item?.Id ?? item?.id ?? 0),
        Nome: String(item?.Nome ?? item?.nome ?? '').trim(),
      }))
      .filter((item: { Id: number; Nome: string }) => item.Id > 0 && item.Nome.length > 0);
  }

  incluirTipo(nome: string): Observable<any> {
    return this.http.post<any>(`${this.executivo}/IncluirTipoPergunta`, { nome }, this.opcoes());
  }

  listarExecutivo(clienteId: string): Observable<any> {
    return this.http.get<any>(`${this.executivo}/Listar/${clienteId}`, this.opcoes());
  }

  abrir(clienteId: string, tipoPerguntaId: number, negocioId: string, titulo: string): Observable<any> {
    return this.http.post<any>(`${this.executivo}/AbrirConversa`, {
      clienteId,
      tipoPerguntaId,
      titulo,
      negocioId: negocioId || null
    }, this.opcoes());
  }

  reservarOperacional(conversaId: string): Observable<any> {
    return this.http.post<any>(`${this.operacional}/Reservar`, { conversaId }, this.opcoes());
  }

  liberarOperacional(conversaId: string): Observable<any> {
    return this.http.post<any>(`${this.operacional}/Liberar`, { conversaId }, this.opcoes());
  }

  enviarExecutivo(conversaId: string, texto: string, somenteAnexo = false): Observable<any> {
    return this.http.post<any>(`${this.executivo}/EnviarMensagem`, { conversaId, texto, somenteAnexo }, this.opcoes());
  }

  encerrarExecutivo(conversaId: string): Observable<any> {
    return this.http.post<any>(`${this.executivo}/Encerrar`, { conversaId }, this.opcoes());
  }

  encerrarOperacional(conversaId: string): Observable<any> {
    return this.http.post<any>(`${this.operacional}/Encerrar`, { conversaId }, this.opcoes());
  }

  marcarExecutivo(conversaId: string): Observable<any> {
    return this.http.post<any>(`${this.executivo}/MarcarLidas`, { conversaId }, this.opcoes());
  }

  listarNotificacoesExecutivo(): Observable<any> {
    return this.http.get<any>(`${this.executivo}/ListarNotificacoes`, this.opcoes());
  }

  marcarNotificacaoExecutivo(mensagemId: string): Observable<any> {
    return this.http.post<any>(`${this.executivo}/MarcarNotificacaoLida`, { mensagemId }, this.opcoes());
  }

  listarOperacional(clienteId: string): Observable<any> {
    return this.http.get<any>(`${this.operacional}/Listar/${clienteId}`, this.opcoes());
  }

  responder(conversaId: string, texto: string, somenteAnexo = false): Observable<any> {
    return this.http.post<any>(`${this.operacional}/Responder`, { conversaId, texto, somenteAnexo }, this.opcoes());
  }

  anexar(lado: 'executivo' | 'operacional', mensagemId: string, arquivos: File[]): Observable<any> {
    const form = new FormData();
    arquivos.forEach(arquivo => form.append('arquivos', arquivo, arquivo.name));
    const base = lado === 'executivo' ? this.executivo : this.operacional;
    return this.http.post<any>(`${base}/Anexar/${mensagemId}`, form, this.opcoes());
  }

  baixarAnexo(lado: 'executivo' | 'operacional', anexoId: string): Observable<Blob> {
    const base = lado === 'executivo' ? this.executivo : this.operacional;
    return this.http.get(`${base}/BaixarAnexo/${anexoId}`, {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true,
      responseType: 'blob'
    });
  }

  obterConfiguracaoAlerta(): Observable<any> {
    return this.http.get<any>(`${this.operacional}/ObterConfiguracaoAlerta`, this.opcoes());
  }

  salvarConfiguracaoAlerta(quantidade: number, unidade: string, tiposUsuario: number[]): Observable<any> {
    return this.http.post<any>(`${this.operacional}/SalvarConfiguracaoAlerta`, {
      quantidade,
      unidade,
      tiposUsuario
    }, this.opcoes());
  }

  listarAlertasPendentes(): Observable<any> {
    return this.http.get<any>(`${this.operacional}/ListarAlertasPendentes`, this.opcoes());
  }

  marcarAlertaVisto(itens: Array<{ conversaId: string; motivo: string; referencia: string }>): Observable<any> {
    return this.http.post<any>(`${this.operacional}/MarcarAlertaVisto`, { itens }, this.opcoes());
  }

  marcarOperacional(clienteId: string): Observable<any> {
    return this.http.post<any>(`${this.operacional}/MarcarLidas`, { clienteId }, this.opcoes());
  }

  listarNotificacoesOperacional(): Observable<any> {
    return this.http.get<any>(`${this.operacional}/ListarNotificacoes`, this.opcoes());
  }

  marcarNotificacaoOperacional(mensagemId: string): Observable<any> {
    return this.http.post<any>(`${this.operacional}/MarcarNotificacaoLida`, { mensagemId }, this.opcoes());
  }

  notificacaoJaVista(mensagemId: string): boolean {
    return this.notificacoesVistas.has(this.normalizarId(mensagemId));
  }

  notificacaoOculta(mensagemId: string): boolean {
    return this.notificacoesOcultas.has(this.normalizarId(mensagemId));
  }

  ocultarNotificacao(mensagemId: string): void {
    const id = this.normalizarId(mensagemId);
    if (!id) {
      return;
    }
    this.notificacoesOcultas.add(id);
  }

  registrarNotificacaoVista(mensagemId: string): void {
    const id = this.normalizarId(mensagemId);
    if (!id || this.notificacoesVistas.has(id)) {
      return;
    }
    this.notificacoesVistas.add(id);
    this.notificacaoVista$.next(id);
  }

  private normalizarId(mensagemId: string): string {
    return (mensagemId || '').trim().toLowerCase();
  }

  private opcoes() {
    return {
      headers: this.requestToken.createAuthorizationHeader(),
      withCredentials: true
    };
  }
}
